import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Heart, Loader2, Star, ExternalLink } from "lucide-react";
import { toast } from "sonner";

/**
 * MyFavoritesCard — customer sees the businesses they've favorited, most
 * recent first. Tapping a card opens the business profile. Rendered on the
 * Customer Dashboard alongside Loyalty / Friends / Prefs.
 */
export default function MyFavoritesCard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/me/customer/following");
        setItems(data?.businesses || []);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <Card className="rounded-md border border-border p-6" data-testid="my-favorites-card">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="h-10 w-10 rounded-full bg-rose-50 text-rose-600 flex items-center justify-center shrink-0">
          <Heart size={18} fill="currentColor" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-display text-xl">My favorite businesses</h3>
          <p className="text-sm text-gray-500 mt-0.5 max-w-2xl">
            Businesses you&apos;ve favorited show up here. You&apos;ll get a push notification
            whenever they post a fresh drop or restock. Favoriting also boosts a
            business&apos;s directory ranking.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="mt-5 flex items-center gap-2 text-sm text-gray-500">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <p className="mt-5 text-sm text-gray-500" data-testid="my-favorites-empty">
          You haven&apos;t favorited any businesses yet. Browse the{" "}
          <Link to="/directory" className="text-[#B31E1E] hover:underline">
            directory
          </Link>{" "}
          and tap the heart to add your first one.
        </p>
      ) : (
        <ul
          className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3"
          data-testid="my-favorites-list"
        >
          {items.map((b) => (
            <li
              key={b.id}
              className="border border-border rounded-sm px-3 py-2.5 flex items-center gap-3"
              data-testid={`my-favorite-row-${b.id}`}
            >
              {b.logo_data_url ? (
                <img
                  src={b.logo_data_url}
                  alt=""
                  className="h-10 w-10 rounded-full object-cover shrink-0"
                />
              ) : (
                <div className="h-10 w-10 rounded-full bg-[#B31E1E]/10 text-[#B31E1E] font-medium flex items-center justify-center shrink-0">
                  {(b.business_name || "?")[0]?.toUpperCase()}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <Link
                  to={`/businesses/${b.id}`}
                  className="text-sm font-medium truncate hover:text-[#B31E1E] transition-colors block"
                >
                  {b.business_name || "—"}
                </Link>
                <div className="text-xs text-gray-500 truncate">
                  {b.category || "—"}
                  {b.rating_count > 0 && (
                    <span className="ml-2 inline-flex items-center gap-0.5 text-gray-400">
                      <Star size={10} className="text-amber-500" fill="currentColor" />
                      {(b.rating_avg || 0).toFixed(1)}
                    </span>
                  )}
                </div>
              </div>
              <Link
                to={`/businesses/${b.id}`}
                className="text-gray-400 hover:text-[#B31E1E] shrink-0"
                aria-label="Open business"
              >
                <ExternalLink size={14} />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
